import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { v4 as uuidv4 } from 'uuid';
import { CONNECTED_ACCOUNT_LOAD_FINISHED } from '../../../constants/actionTypes';
import sanity from '../../../sanity';
import { transformToSanityUrl } from '../../../utils';
import NavDesktop from '../../Nav/NavDesktop';
import NavMobile from '../../Nav/NavMobile';
import Player from '../../Player/Player';
import styles from './watchEvent.module.scss';
import fetch from '../../../fetch';
import premiumStyles from '../premiumContent.module.scss';
import LockIcon from '@mui/icons-material/Lock';
import colors from '../../../utils/colors';
import { fromUnitAmount } from '../../../utils/unitAmountHelper';
import { Button, Chip } from '../../../core/components';

const mapStateToProps = (state) => {
    return {
        company: state.common.company,
        user: state.user.currentUser,
        isAdmin: state.user.currentUser.groups && state.user.currentUser.groups.includes('Admin'),
        connected_account: state.common.connected_account,
        currency: state.common.currency
    };
};

const mapDispatchToProps = (dispatch) => ({
    setConnectedAccount: (payload) => dispatch({ type: CONNECTED_ACCOUNT_LOAD_FINISHED, payload })
});

const WatchEvent = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams();
    const [event, setEvent] = useState();
    const [loading, setLoading] = useState(false);
    const uniqueHash = useMemo(() => uuidv4(), []);
    const [isPortrait, setIsPortrait] = useState(false);
    // const [hasPurchasedVideo, setHasPurchasedVideo] = useState(false);
    const [watchTrailer, setWatchTrailer] = useState(false);
    const [lockVideo, setLockVideo] = useState(false);
    const [fetchLoader, setFetchLoader] = useState(false);
    const [price, setPrice] = useState();
    const [canWatch, setCanWatch] = useState(false);
    const [thumbnail, setThumbnail] = useState();
    const [priceText, setPriceText] = useState();
    const [priceLoading, setPriceLoading] = useState(false);

    const getQueryParams = (search) => {
        return new URLSearchParams(search);
    };
    useEffect(() => {
        const fetchEvent = async () => {
            try {
                setLoading(true);
                const event = await sanity.Events.GetEventById(id);
                setEvent(event);
                const image = sanity.urlFor(event.thumbnail.image_asset.asset);
                const transformedImage = transformToSanityUrl(image.options);
                setThumbnail(transformedImage);
            } catch (error) {
                console.log('ERROR:', error);
            } finally {
                setLoading(false);
            }
        };
        if (id) {
            fetchEvent();
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setPriceLoading(true);
                const priceObject = await fetch.Products.Get(undefined, event.pay_per_view_product);
                setPrice(priceObject.default_price);
                if (props.currency.alignment === 'right') {
                    setPriceText(
                        `${fromUnitAmount(priceObject.default_price?.unit_amount_decimal)} ${
                            priceObject.default_price?.currency
                        }`
                    );
                } else {
                    setPriceText(
                        `${priceObject.default_price?.currency} ${fromUnitAmount(
                            priceObject.default_price?.unit_amount_decimal
                        )}`
                    );
                }
            } catch (error) {
                console.log('ERROR: ', error);
            } finally {
                setPriceLoading(false);
            }
        };
        if (event && props?.connected_account && props.currency.alignment) {
            fetchData();
        }
    }, [event, props.connected_account, props.currency]);

    const fetchSubscription = async () => {
        try {
            setFetchLoader(true);
            const queryParams = getQueryParams(location.search);
            const session_id = queryParams.get('session_id');
            const account = await fetch.StripeAccount.getAccountByCompany(
                process.env.REACT_APP_COMPANY_ID
            );
            props.setConnectedAccount(account);
            const subscriptionStatus = await fetch.StripeAccount.getSubscriptionStatus(
                props.user?.data?.email ?? '',
                process.env.REACT_APP_COMPANY_ID,
                props.user?.groups?.includes('Admin') ? undefined : account?.accountId,
                session_id ?? ''
            );
            const canWatch = subscriptionStatus.events.includes(event.id);
            setCanWatch(canWatch);
            return canWatch;
        } catch (error) {
            console.log('ERROR: ', error);
        } finally {
            setFetchLoader(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (event && props.user.loaded) {
                if (!props.isAdmin) {
                    const hasPurchasedVideo = await fetchSubscription();
                    if (hasPurchasedVideo) {
                        return;
                    }
                    const preventWatch =
                        (props.user.loaded && !props.user?.data) ||
                        !props.user.premium_services?.events.includes(event?.id);
                    if (event?.trailer) {
                        if (preventWatch) {
                            setWatchTrailer(true);
                        }
                    } else {
                        if (preventWatch) {
                            setLockVideo(true);
                        }
                    }
                }
            }
        };
        fetchData();
    }, [props.user, event, props.isAdmin]);

    const handleBuyClick = () => {
        history.push(`/${event.id}/checkout`);
    };

    return (
        <Box>
            {isMobile ? <NavMobile /> : <NavDesktop />}
            <Box className={styles.container}>
                <Box className={styles.main}>
                    <Box className={styles.eventContent}>
                        {event && props.user.loaded && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? '32px !important' : '64px !important',
                                        color: 'white',
                                        lineHeight: isMobile ? '34px' : '74px',
                                        letterSpacing: isMobile ? '-0.64px' : '-1.28px'
                                    }}
                                    fontFamily={'InstrumentSans-semibold'}
                                    textAlign={'center'}>
                                    {event.title}
                                </Typography>
                                {!priceText ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        {!props.isAdmin && !canWatch && (
                                            <Box className={styles.buttonsBox}>
                                                {price && (
                                                    <Box className={styles.buttons}>
                                                        <Button
                                                            title={`${t('Buy')} ${priceText}`}
                                                            onClick={handleBuyClick}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </>
                                )}
                                {!props.user?.data && !canWatch && (
                                    <Typography sx={{ opacity: 0.6 }} textAlign={'center'}>
                                        {t('AlreadyPurchasedThis')}
                                        <Link
                                            to={{
                                                pathname: '/login',
                                                state: { fromWatch: true }
                                            }}>
                                            <Typography
                                                as="span"
                                                className={styles.signInLink}
                                                fontFamily="InstrumentSans-bold">
                                                {t('SignIn')}
                                            </Typography>
                                        </Link>
                                    </Typography>
                                )}
                                <Box className={styles.videoContainer}>
                                    {fetchLoader ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                            <CircularProgress sx={{ color: colors.primary }} />
                                        </Box>
                                    ) : (
                                        <>
                                            {lockVideo ? (
                                                <Box
                                                    className={premiumStyles.wrapper}
                                                    sx={{ backgroundImage: `url(${thumbnail})` }}>
                                                    <Box className={premiumStyles.innerContent}>
                                                        <Box className={premiumStyles.iconWrapper}>
                                                            <LockIcon
                                                                className={premiumStyles.lockIcon}
                                                            />
                                                        </Box>
                                                        <Typography
                                                            className={premiumStyles.textContent}>
                                                            {t('AvailableOnlyForPremium')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Player
                                                    style={{
                                                        borderRadius: '12px'
                                                    }}
                                                    uniqueHash={uniqueHash}
                                                    streamSource={event.live_stream_url}
                                                    showTrailer={watchTrailer}
                                                    muted={false}
                                                    sound={true}
                                                    autoplay={false}
                                                    event={event}
                                                    setIsPortrait={setIsPortrait}
                                                    setLockVideo={setLockVideo}
                                                    thumbnail={thumbnail}
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                                <Typography
                                    sx={{
                                        opacity: 0.6,
                                        fontSize: isMobile ? '16px' : '20px',
                                        lineHeight: 'normal',
                                        letterSpacing: isMobile ? '-0.16px' : '-0.2px'
                                    }}>
                                    {event.description}
                                </Typography>
                                <Box className={styles.creator}>
                                    <Typography color={'white'}>{t('Creator')}</Typography>
                                    <Chip label={props?.company?.name} />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WatchEvent);
